import { HStack, IconButton, StackProps, Text, VStack } from '@chakra-ui/react';
import { DeleteIcon } from '@repo/shared/shared';
import { useTranslation } from 'next-i18next';
import { Image } from '@/shared/ui';
import { useCartStoreActions, useGetCartStore } from '../../hooks/cartStoreHooks';
import { cartStoreSelectors } from '../../selectors';
import CartItemPrice from './CartItemPrice';
import CartItemQuantity from './CartItemQuantity';

interface CartItemProps extends StackProps {
  itemSlug: string;
  hideRemoveButton?: boolean;
}

const CartItem = ({ itemSlug, hideRemoveButton, ...rest }: CartItemProps) => {
  const { t } = useTranslation();
  const item = useGetCartStore(cartStoreSelectors.itemBySlug(itemSlug));
  const { removeItem } = useCartStoreActions();

  const handleRemove = () => {
    removeItem(itemSlug);
  };

  if (!item) {
    return null;
  }

  return (
    <HStack
      _active={{ bg: 'bg.light' }}
      _hover={{ bg: { md: 'bg.light' } }}
      align='start'
      borderRadius='xl'
      borderWidth={1}
      p='sm'
      spacing={4}
      w='full'
      {...rest}
    >
      <Image
        alt={item.title}
        borderRadius='md'
        boxSize='6rem'
        objectFit='cover'
        src={item.imageUrl}
      />

      <VStack
        align='start'
        flex={1}
        spacing={2}
      >
        <Text
          fontSize='md'
          fontWeight='medium'
        >
          {item.title}
          {item.color && (
            <Text
              as='span'
              color='gray.600'
              fontSize='sm'
              fontWeight='normal'
              ml={2}
            >
              ({item.color})
            </Text>
          )}
        </Text>

        <CartItemPrice itemSlug={itemSlug} />
        <HStack w='full'>
          <CartItemQuantity itemSlug={itemSlug} />
          {!hideRemoveButton && (
            <IconButton
              aria-label={t('store.cart.removeItem')}
              icon={<DeleteIcon boxSize={4} />}
              size='xs'
              variant='ghost'
              onClick={handleRemove}
            />
          )}
        </HStack>
      </VStack>
    </HStack>
  );
};

export default CartItem;
